import { Close, Delete, Done } from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { api, isset } from "../util/util";
import { API_CONNECTION, JA_LANG } from "../config/Const";
import { useRecoilState } from "recoil";
import { showLoaderAtom, loginInfoAtom } from "../store/atom";
import { useSnackbar } from "notistack";
import ConfirmDialog from "../component/ConfirmDialog";
// import axios from "axios";

function AccountEditor({
  closeFunc,
  accountId = 0,
}) {
  const { enqueueSnackbar } = useSnackbar();

  let isEdit = accountId !== 0;

  const [showLoader, setShowLoader] = useRecoilState(showLoaderAtom);

  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    email: "",
    password: "",
    authority: "",
    production_id: "",
    created_at: "",
  });

  const [listData, setListData] = useState({});

  const [errorList, setErrorList] = useState({});

  const isManager = useRef(false);

  const [loginInfo] = useRecoilState(loginInfoAtom);

  //初期処理
  useEffect(() => {
    isEdit = accountId !== 0;
    
    if (isEdit) {
      // 編集時：アカウント情報を取得
      setShowLoader(true);
      api({ url: `${API_CONNECTION.URL}api/accounts/${accountId}` })
        .then((res) => {
          setFormData(res.data.account);
          if (isset(res.data.isManager)) {
            isManager.current = res.data.isManager;
          }
          initListData(res.data);
          setShowLoader(false);
        })
        .catch((err) => {
          setShowLoader(false);
        });
    } else {
      // 新規作成時：リストデータのみ取得
      setShowLoader(true);
      api({ url: `${API_CONNECTION.URL}api/accounts/0` })
        .then((res) => {
          if (isset(res.data.isManager)) {
            isManager.current = res.data.isManager;
          }
          initListData(res.data);
          // 新規作成時の初期値
          setFormData({
            id: 0,
            name: "",
            email: "",
            password: "",
            authority: "1", // デフォルト管理者
            production_id: "",
            created_at: "",
          });
          setShowLoader(false);
        })
        .catch((err) => {
          setShowLoader(false);
        });
    }
  }, [accountId]);

  useEffect(() => {
    // ログイン情報が読み込まれた後にログ出力
    if (loginInfo && Object.keys(loginInfo).length > 0) {
        console.log('=== ログイン情報読み込み完了 ===');
        console.log('loginInfo:', loginInfo);
    }
}, [loginInfo]);

  const initListData = (loadData) => {
    const addListData = {};

    for (let loadKey in loadData) {
      if (loadKey.match(/^arr/)) {
        addListData[loadKey] = loadData[loadKey];
      }
    }

    if (addListData.length !== 0) {
      setListData({ ...listData, ...addListData });
    }
  };

  const handleForm = (event) => {
    const { name, value } = event.target;
    
    // 区分変更時のProductionプルダウン自動リセット
    if (name === 'authority' && (value === '1' || value === '2')) {
      setFormData({ 
        ...formData, 
        [name]: value,
        production_id: '' // Productionプルダウンをリセット
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Authorityに応じたProductionプルダウンの制御
  const isProductionDisabled = () => {
    return formData.authority === '1' || formData.authority === '2';
  };

  // 保存時のバリデーション
  const validateForm = () => {
    const errors = {};
    
    // 必須項目チェック
    if (!formData.name || formData.name.trim() === '') {
      errors.name = '名前は必須です';
    }
    
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'メールアドレスは必須です';
    }
    
    if (!isEdit && (!formData.password || formData.password.trim() === '')) {
      errors.password = 'パスワードは必須です';
    }
    
    if (!formData.authority || formData.authority === '') {
      errors.authority = '権限は必須です';
    }
    
    // 事務所区分の場合、Productionが選択されているかチェック
    if (formData.authority === '3' && (!formData.production_id || formData.production_id === '')) {
      errors.production_id = '事務所区分の場合はProductionを選択してください';
    }
    
    return errors;
  };

  const handleSubmit = (event) => {
    //フォームデータ送信時に画面を再更新しないようにする
    event.preventDefault();

    // バリデーション実行
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrorList(validationErrors);
      return;
    }

    setErrorList({});

    setShowLoader(true);

    let apiUrl = `${API_CONNECTION.URL}api/accounts`;
    let apiMethod = "post";

    if (isEdit) {
      apiUrl = `${API_CONNECTION.URL}api/accounts/${formData.id}`;
      apiMethod = "put";
    }

    api({
      url: apiUrl,
      type: apiMethod,
      data: formData,
    })
      .then((res) => {
        setShowLoader(false);

        if (isset(res.data.ret) && res.data.ret === true) {

          //再認証のためリダイレクト
          window.location.href = "/account";
          closeFunc();
          enqueueSnackbar(
            isEdit
              ? "Account update successfully!"
              : "Account regist successfully!",
            {
              variant: "success",
            }
          );
        } else {
          if (isset(res.data.error) && res.data.error) {
            setErrorList(res.data.error);
          }
        }
      })
      .catch(() => {
        setShowLoader(false);
      });
  };

  const handleDelete = (event) => {
    //フォームデータ送信時に画面を再更新しないようにする
    event.preventDefault();

    setErrorList({});
    setShowConfirm(false);

    setShowLoader(true);

    api({
      url: `${API_CONNECTION.URL}api/accounts/${formData.id}`,
      type: "delete",
    })
      .then((res) => {
        setShowLoader(false);

        if (isset(res.data.ret) && res.data.ret === true) {
          //再認証のためリダイレクト
          window.location.href = "/account";

          closeFunc();
          enqueueSnackbar("Account delete successfully!", {
            variant: "success",
          });
        } else {
          if (isset(res.data.error) && res.data.error) {
            setErrorList(res.data.error);
          }
        }
      })
      .catch(() => {
        setShowLoader(false);
      });
  };

  // 管理者区分アカウントでログインしているかチェック
  const isAdminLoggedIn = () => {
    return loginInfo && loginInfo.authority === 1; // 管理者区分
  };

  // 管理者用アカウント作成ボタンの表示条件
  const showCreateAdminButton = () => {
    return isEdit && isAdminLoggedIn();
  };

  // 管理者用アカウント作成ボタンのクリックハンドラー
  const handleCreateAdminAccount = () => {
    console.log('=== 管理者用アカウント作成開始 ===');
    
    api({
        url: `${API_CONNECTION.URL}api/accounts/${accountId}/create-admin-account`,
        type: 'post',
        data: {}  // ← 空のオブジェクトでもOK
    })
    .then((res) => {
        console.log('管理者用アカウント作成成功:', res);
        
        if (res.data.message) {
            alert(res.data.message);
        } else {
            alert('管理者用アカウントを作成しました');
        }
        // アラートのOKボタンを押した後も、アカウント編集画面に滞在する（リダイレクトしない）
    })
    .catch((error) => {
        console.error('管理者用アカウント作成エラー:', error);
        if (error.response && error.response.data && error.response.data.message) {
            alert(error.response.data.message);
        } else {
            alert('エラーが発生しました');
        }
        // エラーの場合も、アカウント編集画面に滞在する（リダイレクトしない）
    });
};
  return (
    <Fragment key={accountId}>
      <AppBar sx={{ position: "relative" }} color="transparent">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeFunc}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {accountId ? "Account : " + formData.name : "Regist Account"}
          </Typography>

          {/* 管理者用アカウント作成ボタン（Saveの左） */}
          {showCreateAdminButton() && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{ mr: "10px" }}
              onClick={handleCreateAdminAccount}
            >
              管理者用アカウント作成
            </Button>
          )}

          <Button
            autoFocus
            color="secondary"
            variant="contained"
            startIcon={<Done />}
            onClick={handleSubmit}
          >
            SAVE
          </Button>
          
          {isEdit && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Delete />}
              size="small"
              sx={{ ml: "10px" }}
              onClick={() => {
                setShowConfirm(true);
              }}
            >
              Delete
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Container component="main">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box component="form" noValidate sx={{ mt: 3 }}>
            <input type="hidden" name="id" value="{formData.id}" />
            <Grid container justify="center" spacing={10}>
              <Grid item xs={12} md={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {isManager.current === true ? (
                      <>
                        <FormControl sx={{ minWidth: "100%" }}>
                          <InputLabel required shrink htmlFor="authority">
                            Authority
                          </InputLabel>

                          <Select
                            native
                            defaultValue=""
                            id="authority"
                            label="Authority"
                            name="authority"
                            value={formData.authority}
                            disabled={!isManager.current}
                            onChange={handleForm}
                          >
                            <option aria-label="" value="">
                              ---
                            </option>
                            {isset(listData.arrAuthority) &&
                              Object.keys(listData.arrAuthority).map(
                                (authorityType) => (
                                  <option
                                    value={authorityType}
                                    key={authorityType}
                                  >
                                    {listData.arrAuthority[authorityType]}
                                  </option>
                                )
                              )}
                          </Select>
                        </FormControl>
                        <Alert
                          variant="outlined"
                          severity="error"
                          className="error-alert"
                          style={{
                            display: isset(errorList.authority) ? "" : "none",
                          }}
                          sx={{ mt: "5px" }}
                        >
                          {errorList.authority}
                        </Alert>
                      </>
                    ) : (
                      <input
                        type="hidden"
                        name="authority"
                        value="{formData.authority}"
                      />
                    )}
                  </Grid>
                  {/* Production選択（Authorityに応じて制御） */}
                  <Grid item xs={12}>
                    <FormControl sx={{ minWidth: "100%" }}>
                      <InputLabel required shrink htmlFor="production_id">
                        Production
                      </InputLabel>
                      <Select
                        native
                        defaultValue=""
                        id="production_id"
                        label="Production"
                        name="production_id"
                        value={formData.production_id}
                        disabled={!isManager.current || isProductionDisabled()}
                        onChange={handleForm}
                      >
                        <option aria-label="" value="">
                          ---
                        </option>
                        {isset(listData.arrProduction) &&
                          Object.keys(listData.arrProduction).map(
                            (productionId) => (
                              <option
                                value={productionId}
                                key={productionId}
                              >
                                {listData.arrProduction[productionId]}
                              </option>
                            )
                          )}
                      </Select>
                    </FormControl>
                    <Alert
                      variant="outlined"
                      severity="error"
                      className="error-alert"
                      style={{
                        display: isset(errorList.production_id) ? "" : "none",
                      }}
                      sx={{ mt: "5px" }}
                    >
                      {errorList.production_id}
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id="name"
                      label="Name"
                      name="name"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleForm}
                    />
                    <Alert
                      variant="outlined"
                      severity="error"
                      className="error-alert"
                      style={{
                        display: isset(errorList.name) ? "" : "none",
                      }}
                      sx={{ mt: "5px" }}
                    >
                      {errorList.name}
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    {/* <FormLabel>Email</FormLabel> */}
                    <TextField
                      required
                      fullWidth
                      type="email"
                      id="email"
                      label="Email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleForm}
                    />
                    <Alert
                      variant="outlined"
                      severity="error"
                      className="error-alert"
                      style={{ display: isset(errorList.email) ? "" : "none" }}
                      sx={{ mt: "5px" }}
                    >
                      {errorList.email}
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    {/* <FormLabel>Password</FormLabel> */}
                    <TextField
                      required={accountId ? false : true}
                      fullWidth
                      name="password"
                      label={'Password ' + (accountId ? '※' + JA_LANG.ENTER_ONLY_FOR_UPDATE : '')}
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={handleForm}
                      autoComplete="new-password"
                    />
                    <Alert
                      variant="outlined"
                      severity="error"
                      className="error-alert"
                      style={{
                        display: isset(errorList.password) ? "" : "none",
                      }}
                      sx={{ mt: "5px" }}
                    >
                      {errorList.password}
                    </Alert>
                  </Grid>
                  {isEdit && (
                    <Grid item xs={12}>
                      <InputLabel shrink>Created at</InputLabel>
                      {formData.created_at}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
      <ConfirmDialog
        open={showConfirm}
        setOpen={setShowConfirm}
        okFunc={handleDelete}
        titleText={JA_LANG.CONFIRM_DELETE_TITLE}
      />
    </Fragment>
  );
}

export default AccountEditor;
